import {
  clamp,
  copyV3,
  dotV3,
  FORWARD,
  lengthV3,
  quatRotateVec3,
  RIGHT,
  subV3,
  UP,
  v3,
  type Vec3,
} from './math.js';
import { Rng } from './rng.js';
import type { Vehicle } from './vehicle.js';
import { makeInput, type VehicleInput } from './input.js';

/**
 * Per-skill AI tuning. All angles in radians.
 *
 * Higher tiers = faster, tighter, more aggressive use of boost and drift.
 * Lower tiers = slower, wider, less precise — they lose on purpose, not by accident.
 */
export interface AISkillProfile {
  /** Human-readable name. */
  name: string;
  /** Cap on |throttle| output. 1 = unrestricted. */
  throttleMax: number;
  /** Reverse throttle when stuck (negative). */
  reverseThrottle: number;
  /** Multiplier on heading-error → steer. */
  steerGain: number;
  /** Steering jitter magnitude added each tick (deterministic via RNG). */
  steerJitter: number;
  /** Brake when angle to target exceeds this (radians). */
  brakeAtAngle: number;
  /** Light off-throttle (no brake) coast above this angle. */
  coastAtAngle: number;
  /** Boost only when angle to next waypoint is BELOW this (radians). */
  boostStraightThreshold: number;
  /** Don't boost below this speed (m/s) — wastes boost. */
  boostMinSpeed: number;
  /** Engage drift when |steer output| above this. */
  driftAtSteer: number;
  /** Advance to next waypoint when within this distance (m). */
  waypointRadius: number;
  /** How many waypoints ahead to "look" for cornering. */
  lookahead: number;
  /** Stuck timer threshold (ms of low speed → reverse). */
  stuckThresholdMs: number;
  /** While airborne, push throttle forward for a front flip (true = does flips). */
  flipsInAir: boolean;
  /** While airborne, also apply roll input for barrel rolls (true = does flips + rolls). */
  rollsInAir: boolean;
}

export const AI_SKILLS: Record<string, AISkillProfile> = {
  trainee: {
    name: 'Trainee',
    throttleMax: 0.55,
    reverseThrottle: -0.5,
    steerGain: 1.4,
    steerJitter: 0.18,
    brakeAtAngle: 0.5,
    coastAtAngle: 0.35,
    boostStraightThreshold: -1, // never boosts
    boostMinSpeed: 999,
    driftAtSteer: 999,
    waypointRadius: 8,
    lookahead: 1,
    stuckThresholdMs: 2000,
    flipsInAir: false,
    rollsInAir: false,
  },
  rookie: {
    name: 'Rookie',
    throttleMax: 0.78,
    reverseThrottle: -0.6,
    steerGain: 1.8,
    steerJitter: 0.07,
    brakeAtAngle: 0.7,
    coastAtAngle: 0.45,
    boostStraightThreshold: 0.22,
    boostMinSpeed: 8,
    driftAtSteer: 0.85,
    waypointRadius: 6,
    lookahead: 2,
    stuckThresholdMs: 1500,
    flipsInAir: false,
    rollsInAir: false,
  },
  ace: {
    name: 'Ace',
    throttleMax: 0.94,
    reverseThrottle: -0.7,
    steerGain: 2.4,
    steerJitter: 0.025,
    brakeAtAngle: 0.9,
    coastAtAngle: 0.55,
    boostStraightThreshold: 0.32,
    boostMinSpeed: 10,
    driftAtSteer: 0.7,
    waypointRadius: 5,
    lookahead: 2,
    stuckThresholdMs: 1200,
    flipsInAir: true,
    rollsInAir: false,
  },
  champion: {
    name: 'Champion',
    throttleMax: 1.0,
    reverseThrottle: -0.8,
    steerGain: 3.0,
    steerJitter: 0,
    brakeAtAngle: 1.05,
    coastAtAngle: 0.65,
    boostStraightThreshold: 0.42,
    boostMinSpeed: 12,
    driftAtSteer: 0.55,
    waypointRadius: 4.5,
    // lookahead=1 → drives directly through each waypoint with no path
    // smoothing. Combined with ramp-centric waypoints, ensures Champion
    // launches off every ramp instead of cutting the corner.
    lookahead: 1,
    stuckThresholdMs: 900,
    flipsInAir: true,
    rollsInAir: true,
  },
};

export const AI_SKILL_NAMES = ['trainee', 'rookie', 'ace', 'champion'] as const;
export type AISkillName = (typeof AI_SKILL_NAMES)[number];

/**
 * Waypoint-following AI. Reads vehicle pose + linvel each tick, produces an input.
 *
 * It does NOT directly mutate the vehicle. Use the returned input by
 * passing it to sim.setInput(carId, input) — same path as a real player.
 */
export class AIDriver {
  readonly vehicle: Vehicle;
  readonly waypoints: readonly Vec3[];
  readonly skill: AISkillProfile;
  readonly rng: Rng;
  private output: VehicleInput = makeInput();
  current = 0;
  private stuckTimerMs = 0;
  private reverseTimerMs = 0;

  constructor(
    vehicle: Vehicle,
    waypoints: readonly Vec3[],
    skill: AISkillProfile,
    rngSeed: number,
  ) {
    if (waypoints.length === 0) throw new Error('AIDriver needs at least 1 waypoint');
    this.vehicle = vehicle;
    this.waypoints = waypoints;
    this.skill = skill;
    this.rng = new Rng(rngSeed);
    this.current = nearestWaypointIndex(vehicle.position, waypoints);
  }

  step(dtSec: number): VehicleInput {
    const out = this.output;
    const cfg = this.skill;
    const pos = this.vehicle.position;
    const target = this.waypoints[this.current]!;

    // Advance waypoint if reached.
    const toTarget = v3(target.x - pos.x, target.y - pos.y, target.z - pos.z);
    toTarget.y = 0;
    const dist = lengthV3(toTarget);
    if (dist < cfg.waypointRadius) {
      this.current = (this.current + 1) % this.waypoints.length;
    }

    // Build "look ahead" target — average of next N waypoints, weighted.
    // This is what lets higher-skill AI cut corners.
    let aimX = 0;
    let aimZ = 0;
    let weightSum = 0;
    for (let i = 0; i < cfg.lookahead; i++) {
      const idx = (this.current + i) % this.waypoints.length;
      const wp = this.waypoints[idx]!;
      const w = 1 / (i + 1);
      aimX += wp.x * w;
      aimZ += wp.z * w;
      weightSum += w;
    }
    aimX /= weightSum;
    aimZ /= weightSum;

    const aimDx = aimX - pos.x;
    const aimDz = aimZ - pos.z;
    const aimLen = Math.hypot(aimDx, aimDz);
    const aimNx = aimDx / Math.max(aimLen, 1e-4);
    const aimNz = aimDz / Math.max(aimLen, 1e-4);

    // Compute car forward in world space (flattened).
    const fwd = v3();
    quatRotateVec3(fwd, this.vehicle.quaternion, FORWARD as Vec3);
    fwd.y = 0;
    const fwdLen = Math.hypot(fwd.x, fwd.z);
    const fwdNx = fwd.x / Math.max(fwdLen, 1e-4);
    const fwdNz = fwd.z / Math.max(fwdLen, 1e-4);

    // Angle between fwd and aim — sign indicates left/right.
    const cross = fwdNx * aimNz - fwdNz * aimNx; // y component of fwd × aim
    const dot = fwdNx * aimNx + fwdNz * aimNz;
    // Clamp for acos stability.
    const angle = Math.acos(clamp(dot, -1, 1)); // 0..π
    // Steering direction: right is positive — when cross > 0, target is to our right.
    const steerSign = cross > 0 ? 1 : -1;

    // Stuck detection: low speed for too long → reverse a bit, then continue.
    const speed = lengthV3(this.vehicle.linvel);
    if (speed < 1.2) this.stuckTimerMs += dtSec * 1000;
    else this.stuckTimerMs = 0;

    if (this.stuckTimerMs > cfg.stuckThresholdMs && this.reverseTimerMs === 0) {
      this.reverseTimerMs = 700; // back up for 0.7s
      this.stuckTimerMs = 0;
    }

    if (this.reverseTimerMs > 0) {
      out.throttle = cfg.reverseThrottle;
      out.steer = -steerSign * 0.6; // counter-steer
      out.drift = false;
      out.boost = false;
      out.brake = false;
      out.reset = false;
      out.lookBack = false;
      this.reverseTimerMs = Math.max(0, this.reverseTimerMs - dtSec * 1000);
      return out;
    }

    // Steering output: clamp(angle * gain, 0, 1), signed.
    const jitter = cfg.steerJitter > 0 ? (this.rng.next() - 0.5) * 2 * cfg.steerJitter : 0;
    const steerMag = clamp(angle * cfg.steerGain, 0, 1);
    let steer = steerSign * steerMag + jitter;
    steer = clamp(steer, -1, 1);

    // Throttle by angle: full ahead on straights, coast or brake on tight turns.
    let throttle: number;
    let brake = false;
    if (angle > cfg.brakeAtAngle && speed > 5) {
      throttle = 0;
      brake = true;
    } else if (angle > cfg.coastAtAngle) {
      throttle = cfg.throttleMax * 0.55;
    } else {
      throttle = cfg.throttleMax;
    }

    // Boost on straights when going fast enough.
    const boost =
      cfg.boostStraightThreshold > 0 &&
      angle < cfg.boostStraightThreshold &&
      speed > cfg.boostMinSpeed;

    // Drift when steering hard.
    const drift = Math.abs(steer) > cfg.driftAtSteer && speed > 6;

    // Airborne stunt override: replace ground inputs with flip/roll commands.
    // This is what produces the visible "AI doing tricks" effect.
    if (!this.vehicle.isGrounded && this.vehicle.airTimeMs > 140) {
      if (cfg.flipsInAir) {
        // Push throttle forward → front-flip pitch. Champions hold full; Ace partial.
        out.throttle = cfg.rollsInAir ? 1 : 0.85;
      } else {
        out.throttle = 0;
      }
      if (cfg.rollsInAir) {
        // Champions add a barrel roll for combo flair.
        out.steer = 0.8;
      } else {
        out.steer = 0;
      }
      out.drift = false;
      out.brake = false;
      out.boost = false;
    } else {
      out.throttle = throttle;
      out.steer = steer;
      out.drift = drift;
      out.boost = boost;
      out.brake = brake;
    }
    out.reset = false;
    out.lookBack = false;

    // Silence "unused import" lint paths.
    void UP;
    void RIGHT;
    void copyV3;
    void subV3;
    void dotV3;

    return out;
  }
}

function nearestWaypointIndex(pos: Vec3, wps: readonly Vec3[]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < wps.length; i++) {
    const wp = wps[i]!;
    const dx = wp.x - pos.x;
    const dz = wp.z - pos.z;
    const d = dx * dx + dz * dz;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
